import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Selection, SelectionMode } from "@fluentui/react/lib/Selection";

import { IProject } from "Model/Storage/IProject";
import { uiSetSelectedProjectIds } from "Store/Actions";
import { selectSelectedOrActiveProjectIds } from "Store/Selectors/Projects";
import { toIdList } from "Utils/Project";
import { useAuthenticationPreflight } from "Components/Generic/Authentication";

export function useSelection(): Selection {
  const { preflightAuthentication } = useAuthenticationPreflight();
  const dispatch = useDispatch();
  const selectedProjectIds = useSelector(selectSelectedOrActiveProjectIds);

  const selection = useMemo(
    () => new Selection<IProject>({
      selectionMode: SelectionMode.multiple,
      getKey(item) {
        return item[`@id`];
      },
      async onSelectionChanged() {
        const gridSelectedProjectIds = toIdList(selection.getSelection());
        await preflightAuthentication();
        dispatch(uiSetSelectedProjectIds(gridSelectedProjectIds));
      }
    }),
    [dispatch, preflightAuthentication]
  );

  useEffect(() => {
    const gridSelectedProjectIds = toIdList(selection.getSelection());
    const gridCount = gridSelectedProjectIds?.length ?? 0;
    const storeCount = selectedProjectIds?.length ?? 0;

    if (storeCount === 0 && gridCount > 0) {
      selection.setChangeEvents(false, true);
      selection.setAllSelected(false);
      selection.setChangeEvents(true, true);
    }

    if (selectedProjectIds.every((id) => gridSelectedProjectIds.includes(id))) {
      return;
    }

    selection.setChangeEvents(false, true);
    for (const id of selectedProjectIds) {
      selection.setKeySelected(id, true, false);
    }
    selection.setChangeEvents(true, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectIds]);

  return selection;
}
